import React, { useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import history from 'routing/history';
import Button, { BackButton } from 'components/Button';
import { H6 } from 'components/Typography';
import EditClaimError from '../../components/EditClaimError';
import ViewAnimator from '../../components/ViewAnimator';
import useNavigateToNextStep from '../../hooks/useNavigateToNextStep';
import useNextAndPrevUrl from '../../hooks/useNextAndPrevUrl';
import { setClaimInfo, editClaim, addValidStep, removeValidStep } from '../../state';
import { STEP_PATHS } from '../../consts';
import { LoadingOverlay, InstructionList, TextArea, Buttons } from './styled';
import { StepHeading, NavigationButtons } from '../../styled';

const validation = Yup.object({
  isOtherParties: Yup.boolean(),
  otherParties: Yup.string().when('isOtherParties', {
    is: true,
    then: Yup.string().required('Required')
  })
});

const OtherParties = () => {
  const dispatch = useDispatch();
  const { claimType, step } = useParams();
  const { nextUrl, prevUrl } = useNextAndPrevUrl(claimType, step);

  const { otherParties } = useSelector((state) => state.nomadInsurance.claims.claimInfo);
  const { claimID, editError, isLoading } = useSelector(
    (state) => state.nomadInsurance.claims
  );
  useNavigateToNextStep(nextUrl, editError);

  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        otherParties,
        isOtherParties: Boolean(otherParties)
      }),
      [otherParties] // eslint-disable-line
    ),
    onSubmit: async (info) => {
      const editedInfo = { otherParties: info.otherParties };
      if (claimID) {
        await dispatch(editClaim({ claimID, editedInfo }));
      } else {
        dispatch(setClaimInfo(editedInfo));
        history.push(nextUrl);
      }
    },
    validateOnMount: true,
    validationSchema: validation
  });

  useEffect(() => {
    if (formik.isValid) {
      dispatch(addValidStep(STEP_PATHS.OTHER_PARTIES));
    } else {
      dispatch(removeValidStep(STEP_PATHS.OTHER_PARTIES));
    }
  }, [formik.isValid, dispatch]);

  const onBack = () => {
    history.push(prevUrl);
  };

  return (
    <ViewAnimator>
      <LoadingOverlay isLoading={isLoading} variant="light" />
      <StepHeading>Other parties</StepHeading>
      <H6>Do the following scenarios apply to you:</H6>
      <InstructionList>
        <li>
          Have you made a claim against any other party with respect to this event? If
          yes, please provide more information about the claim.
        </li>
        <li>
          Are you covered under another insurance policy for this type of incident (e.g.,
          your own, a spouse's, through work)? If yes, please provide details on the
          policy and what it covers.
        </li>
      </InstructionList>
      <Buttons>
        <Button
          variant="flat"
          onClick={() => formik.setFieldValue('isOtherParties', true)}
          $selected={formik.values.isOtherParties}
        >
          <b>Yes</b>
        </Button>
        <Button
          variant="flat"
          onClick={() => {
            formik.setFieldValue('isOtherParties', false);
            formik.setFieldValue('otherParties', '');
          }}
          $selected={!formik.values.isOtherParties}
        >
          <b>No</b>
        </Button>
      </Buttons>
      {formik.values.isOtherParties && (
        <TextArea
          name="otherParties"
          formik={formik}
          value={formik.values.otherParties}
          placeholder="Write a detailed description here"
          variant="outline"
        />
      )}
      <EditClaimError error={editError} isVisible={Boolean(editError)} />
      <NavigationButtons>
        <BackButton onClick={onBack} variant="dark">
          Back
        </BackButton>
        <Button
          onClick={() => formik.submitForm()}
          disabled={(formik.values.isOtherParties && !formik.isValid) || isLoading}
        >
          Save & next
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default OtherParties;
